import React, { useMemo, useState } from "react";
import moment, { Moment } from "moment";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Popover,
  Typography,
  SxProps,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import colors from "../../../../theme/Colours";

interface CustomDateRangePickerProps {
  fromDate?: Date;
  toDate?: Date;
  setFromDate: (date: Date | undefined) => void;
  setToDate: (date: Date | undefined) => void;
  placeholder?: string;
  disableFuture?: boolean;
  showReset?: boolean;
  referenceFromDate?: Date;
  referenceToDate?: Date;
  referenceLabel?: string;
  sx?: SxProps;
}

const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function CustomDateRangePicker({
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  placeholder = "Select date range",
  disableFuture = false,
  showReset = true,
  referenceFromDate,
  referenceToDate,
  referenceLabel = "Period 1",
  sx,
}: CustomDateRangePickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [leftMonth, setLeftMonth] = useState(moment().startOf("month"));

  const [tempFrom, setTempFrom] = useState<Moment | null>(
    fromDate ? moment(fromDate) : null
  );
  const [tempTo, setTempTo] = useState<Moment | null>(
    toDate ? moment(toDate) : null
  );

  const open = Boolean(anchorEl);

  const rightMonth = useMemo(
    () => leftMonth.clone().add(1, "month"),
    [leftMonth]
  );

  const referenceFrom = referenceFromDate ? moment(referenceFromDate) : null;
  const referenceTo = referenceToDate ? moment(referenceToDate) : null;

  const today = moment();

  const isInReferenceSelection = (day: Moment) =>
    !!(
      referenceFrom &&
      referenceTo &&
      day.isSameOrAfter(referenceFrom, "day") &&
      day.isSameOrBefore(referenceTo, "day")
    );

  const wouldOverlapReferenceRange = (start: Moment, end: Moment) =>
    !!(
      referenceFrom &&
      referenceTo &&
      start.isSameOrBefore(referenceTo, "day") &&
      end.isSameOrAfter(referenceFrom, "day")
    );

  const displayValue =
    fromDate && toDate
      ? `${moment(fromDate).format("DD/MM/YYYY")} – ${moment(toDate).format(
          "DD/MM/YYYY"
        )}`
      : "";

  const previewValue =
    tempFrom && tempTo
      ? `${tempFrom.format("DD/MM/YYYY")} to ${tempTo.format("DD/MM/YYYY")}`
      : "Select start and end date";

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTempFrom(fromDate ? moment(fromDate) : null);
    setTempTo(toDate ? moment(toDate) : null);

    if (fromDate) {
      setLeftMonth(moment(fromDate).startOf("month"));
    } else if (referenceFromDate) {
      setLeftMonth(moment(referenceFromDate).startOf("month"));
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateClick = (day: Moment) => {
    if (disableFuture && day.isAfter(moment(), "day")) return;
    if (isInReferenceSelection(day)) return;

    if (!tempFrom || (tempFrom && tempTo)) {
      setTempFrom(day);
      setTempTo(null);
      return;
    }

    const start = day.isBefore(tempFrom, "day") ? day : tempFrom;
    const end = day.isBefore(tempFrom, "day") ? tempFrom : day;

    if (wouldOverlapReferenceRange(start, end)) return;

    setTempFrom(start);
    setTempTo(end);
  };

  const handleApply = () => {
    if (!tempFrom || !tempTo) return;

    setFromDate(tempFrom.toDate());
    setToDate(tempTo.toDate());
    handleClose();
  };

  const handleCancel = () => {
    setTempFrom(fromDate ? moment(fromDate) : null);
    setTempTo(toDate ? moment(toDate) : null);
    handleClose();
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setTempFrom(null);
    setTempTo(null);
    setFromDate(undefined);
    setToDate(undefined);
  };

  const isSelectedStart = (day: Moment) => tempFrom?.isSame(day, "day");
  const isSelectedEnd = (day: Moment) => tempTo?.isSame(day, "day");

  const isInRange = (day: Moment) =>
    !!(
      tempFrom &&
      tempTo &&
      day.isAfter(tempFrom, "day") &&
      day.isBefore(tempTo, "day")
    );

  const isReferenceStart = (day: Moment) => !!referenceFrom?.isSame(day, "day");

  const isReferenceEnd = (day: Moment) => !!referenceTo?.isSame(day, "day");

  const isInReferenceRange = (day: Moment) =>
    !!(
      referenceFrom &&
      referenceTo &&
      day.isAfter(referenceFrom, "day") &&
      day.isBefore(referenceTo, "day")
    );

  const renderMonth = (month: Moment) => {
    const start = month.clone().startOf("month");
    const end = month.clone().endOf("month");
    const firstDay = start.day();
    const daysInMonth = end.date();

    const cells: Array<Moment | null> = [];

    for (let i = 0; i < firstDay; i++) cells.push(null);

    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(month.clone().date(day));
    }

    return (
      <Box sx={{ width: 220 }}>
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: 14,
            mb: 2,
          }}
        >
          {month.format("MMMM YYYY")}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            rowGap: 1,
          }}
        >
          {weekDays.map((weekDay) => (
            <Typography
              key={weekDay}
              sx={{
                fontSize: 11,
                color: "#9aa4b2",
                textAlign: "center",
                fontWeight: 700,
              }}
            >
              {weekDay}
            </Typography>
          ))}

          {cells.map((day, index) => {
            if (!day) return <Box key={index} sx={{ height: 32 }} />;

            const selectedStart = isSelectedStart(day);
            const selectedEnd = isSelectedEnd(day);
            const selected = selectedStart || selectedEnd;
            const range = isInRange(day);

            const referenceStart = isReferenceStart(day);
            const referenceEnd = isReferenceEnd(day);
            const referenceRange = isInReferenceRange(day);
            const referenceSelected = referenceStart || referenceEnd;

            const isToday = day.isSame(today, "day");
            const overlapsReference = isInReferenceSelection(day);

            const disabled =
              (disableFuture && day.isAfter(moment(), "day")) ||
              overlapsReference;
            return (
              <Box
                key={day.format("YYYY-MM-DD")}
                onClick={() => !disabled && handleDateClick(day)}
                sx={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  cursor: overlapsReference ? "not-allowed" : "pointer",
                  color: overlapsReference
                    ? "#c7cdd4"
                    : disabled
                      ? "#c7cdd4"
                      : "#293241",
                  backgroundColor: range
                    ? "#fff1ea"
                    : referenceRange
                      ? "#eef4ff"
                      : "transparent",
                  borderRadius: range || referenceRange ? 0 : "50%",
                  opacity: disabled ? 0.5 : 1,
                  "& span": {
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    backgroundColor: selected
                      ? "#ff6847"
                      : referenceSelected
                        ? "#3b82f6"
                        : "transparent",
                    color: selected || referenceSelected ? "#fff" : "inherit",
                    border: selected
                      ? "none"
                      : isToday
                        ? "1px solid #ff6847"
                        : tempFrom && !tempTo && selectedStart
                          ? "1px solid #ff6847"
                          : "none",
                  },
                  "&:hover span": {
                    border: disabled ? "none" : "1px solid #ff6847",
                  },
                }}
              >
                <span>{day.date()}</span>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          height: 42,
          width: "100%",
          border: "1px solid #ccd5dd",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          px: 1.5,
          cursor: "pointer",
          backgroundColor: "#fff",
          color: displayValue ? "#293241" : "#9aa4b2",
          fontSize: 13,
          ...sx,
        }}
      >
        <InputAdornment position="start" sx={{ mr: 1 }}>
          <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "#667085" }} />
        </InputAdornment>

        <Box
          sx={{
            flex: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: "Poppins, sans-serif",
            color: displayValue ? colors.searchInputText : "#9aa4b2",
          }}
        >
          {" "}
          {displayValue || placeholder}
        </Box>

        {showReset && displayValue && (
          <CloseIcon
            onClick={handleClear}
            sx={{ fontSize: 16, color: "#9aa4b2" }}
          />
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 520,
            borderRadius: "8px",
            boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.14)",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <IconButton
              size="small"
              onClick={() =>
                setLeftMonth((prev) => prev.clone().subtract(1, "month"))
              }
              sx={{ border: "1px solid #e1e6eb", borderRadius: "6px" }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              onClick={() =>
                setLeftMonth((prev) => prev.clone().add(1, "month"))
              }
              sx={{ border: "1px solid #e1e6eb", borderRadius: "6px" }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", gap: 4 }}>
            {renderMonth(leftMonth)}
            {renderMonth(rightMonth)}
          </Box>
        </Box>

        {referenceFrom && referenceTo && (
          <Box
            sx={{
              px: 2,
              pb: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: 12,
              color: "#667085",
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#3b82f6",
              }}
            />
            <span>
              {referenceLabel}: {referenceFrom.format("DD/MM/YYYY")} to{" "}
              {referenceTo.format("DD/MM/YYYY")}
            </span>
          </Box>
        )}

        <Box
          sx={{
            borderTop: "1px solid #e5e7eb",
            px: 2,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {previewValue}
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" size="small" onClick={handleCancel}>
              Cancel
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={handleApply}
              disabled={!tempFrom || !tempTo}
              sx={{
                backgroundColor: "#ff6847",
                "&:hover": { backgroundColor: "#f15d3e" },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
